import {
  Box,
  Checkbox,
  Chip,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/joy";
import { useController, useFormContext } from "react-hook-form";

import Warning from "@/assets/icons/Warning";

import FormGroup from "./FormGroup";
import MultipleValuesSelectedIndicator from "./MultipleValuesSelected";

type Props = {
  name?: string;
  options: { name: string; value: string }[];
  disabled?: boolean;
} & (
  | {
      multiple: true;
      value: string[];
      onChange: (value: string[]) => void;
    }
  | {
      multiple?: false;
      value: string | null;
      onChange: (value: string | null) => void;
    }
);

export function PillsSelect({ name, options, multiple, value, onChange, disabled }: Props) {
  if (!multiple) {
    return (
      <RadioGroup
        name={name}
        value={value || ""}
        sx={{ display: "flex", flexFlow: "row wrap", gap: 0.5 }}
      >
        {options.map((option) => (
          <Chip
            key={option.value}
            variant="outlined"
            sx={{
              paddingY: 0.5,
              fontWeight: "bold",
              paddingTop: 0.5,
            }}
          >
            <Radio
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                if ("value" in e.target && e.target.value === value) {
                  onChange(null);
                } else {
                  onChange(option.value);
                }
              }}
              label={option.name}
              value={option.value}
              disableIcon
              overlay
              slotProps={{
                label: ({ checked }) => ({
                  sx: {
                    fontSize: 14,
                    color: checked ? "white" : "var(--joy-palette-neutral-900)",
                  },
                }),
                action: ({ checked }) => ({
                  sx: {
                    background: checked
                      ? "var(--joy-palette-primary-solidGradientBg)"
                      : "transparent",
                    borderColor: checked ? "transparent" : "var(--joy-palette-neutral-300)",
                  },
                }),
              }}
            />
          </Chip>
        ))}
      </RadioGroup>
    );
  }
  return (
    <Box role="group">
      {options.map((option) => (
        <Chip key={option.value}>
          <Checkbox
            disableIcon
            overlay
            label={option.name}
            value={option.value}
            checked={value?.includes(option.value)}
            onChange={(_e) => {
              if (onChange) {
                if (value) {
                  onChange(
                    value.includes(option.value)
                      ? value.filter((v) => v !== option.value)
                      : [...value, option.value],
                  );
                }
              }
            }}
          />
        </Chip>
      ))}
    </Box>
  );
}

type InputProps = {
  name: string;
  label: string;
  emptyWarning?: string;
  disabled?: boolean;
} & Props;

export const PillsSelectInput = ({
  name,
  label,
  emptyWarning,
  ...props
}: Omit<InputProps, "onChange" | "value">) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel>
        {label}
        {!field.value && emptyWarning ? (
          <Tooltip title={emptyWarning} size="sm" placement="bottom-start">
            <Warning sx={{ marginLeft: 0.5 }} size="sm" />
          </Tooltip>
        ) : null}
      </FormLabel>
      <MultipleValuesSelectedIndicator name={name} />
      {/* Fixme - issue with multiple prop */}
      {/* @ts-expect-error fixme */}
      <PillsSelect {...props} value={field.value} onChange={(value) => field.onChange(value)} />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
